import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getSelectedBoardId } from '@common-reducers/BoardsSelectors';
import { DBTaskColumnThunks } from "@common-reducers/DBServiceThunks";
import { getDynamicColumnListForBTCheckbox } from '@common-reducers/TaskSelectors';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { usePortalContainer } from "@/common/hooks/use-portal-container";
import { useMenuTriggerTooltip } from "@common/components/menu-trigger-tooltip/use-menu-trigger-tooltip";
import { Columns } from "lucide-react"; // Import View as fallback/alternative
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";

export default function ColumnsDropdownShadcn() {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    // "Hide columns" re-opened when the closed menu handed focus back to the button.
    const tip = useMenuTriggerTooltip();
    
    // Selectors logic from BoardHideColumnsDialog
    const columnList = useAppSelector((state) => getDynamicColumnListForBTCheckbox(state, 'board'));

    // Portal logic
    const localPortalContainer = usePortalContainer();
    const [portalContainer, setPortalContainer] = useState<HTMLElement | ShadowRoot | null>(null);

    useEffect(() => {
        const globalPortal = document.getElementById('labels-modals')?.shadowRoot?.querySelector('.btcss-portal-container');
        if (globalPortal) {
            setPortalContainer(globalPortal as HTMLElement);
        }
    }, []);

    const handleCheckBoxChange = (value: boolean, column: any) => {
        dispatch(DBTaskColumnThunks.patch({
            entity: {
                ...column,
                isVisible: value
            }
        }));
    };

    return (
        <DropdownMenu onOpenChange={tip.onMenuOpenChange}>
            <TooltipProvider>
                <Tooltip open={tip.tooltipOpen} onOpenChange={(open) => (open ? tip.onTooltipOpen() : tip.onTooltipClose())}>
                    <TooltipTrigger asChild>
                        <span onPointerEnter={tip.onTriggerPointerEnter}>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-9 px-3 opacity-50 hover:opacity-100">
                                    <Columns className="h-4 w-4 mr-2" />
                                    <span className="text-sm font-medium">{t('tasksPanelHeader.columns')}</span>
                                </Button>
                            </DropdownMenuTrigger>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                        <p>{t('tasksPanelHeader.hideColumnsTooltip')}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>

            <DropdownMenuContent 
                align="start" 
                side="bottom"
                className="w-56 z-[1550]"
                container={portalContainer || localPortalContainer}
            >
                {columnList.map((column) => (
                    <DropdownMenuCheckboxItem
                        key={column._id}
                        checked={column.isVisible ?? true}
                        onCheckedChange={(checked) => handleCheckBoxChange(checked, column)}
                    >
                        {column.title}
                    </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
